import React from 'react';
import Modal from '../../../../components/modals/Modal';

import './AutoBundlerOptionsModal.scss';
import Fetch from '../../../../utils/fetch';
import { settings } from '../../../../settings';
import { classNames } from '../../../../utils/classUtils';
import { queryStringFromObject } from 'utils/urlUtil';
import { Region } from 'types/orderTypes';

interface Props {
  skipCreate?: boolean;
  onCloseModal: () => void;
  onOptionsSelected: (
    timePerStopMinutes: number,
    hubDeparture: string,
    createBundles: boolean
  ) => void;
  region: Region;
}

interface State {
  fieldErrors: {
    [key: string]: string;
  };
  timePerStop: string;
  hubDeparture: string;
  createBundles: boolean;
}

interface RoutingStorage {
  timePerStopMinutes: number;
}

export default class AutoBundlerOptionsModal extends React.Component<
  Props,
  State
> {
  private static readonly DEFAULT_TIME_PER_STOP_MINUTES = 4;

  constructor(props: Props) {
    super(props);

    this.state = {
      fieldErrors: {},
      timePerStop: '',
      hubDeparture: '17:00',
      createBundles: props.skipCreate === undefined ? true : false,
    };
    this.escFunction = this.escFunction.bind(this);
  }

  escFunction(event) {
    if (event.keyCode === 27) {
      this.props.onCloseModal();
    }
  }

  async componentDidMount() {
    document.addEventListener('keydown', this.escFunction, false);
    const timePerStop = await this.fetchRoutingTimePerStop();
    this.setState({ timePerStop: timePerStop.toString() });
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.escFunction, false);
  }

  async fetchRoutingTimePerStop(): Promise<number> {
    const queryParams = queryStringFromObject({
      region: this.props.region,
    });
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/bundle/routing-info${queryParams}`,
    });
    const routingInfo: RoutingStorage = response.ok
      ? await response.json()
      : null;
    return (
      (routingInfo && routingInfo.timePerStopMinutes) ||
      AutoBundlerOptionsModal.DEFAULT_TIME_PER_STOP_MINUTES
    );
  }

  onChangeTimePerStop(value: string) {
    const { fieldErrors } = this.state;
    if (value.trim() === '') {
      delete fieldErrors.timePerStop;
      this.setState({
        timePerStop: '',
        fieldErrors: {
          ...fieldErrors,
          timePerStop: 'Invalid value',
        },
      });
      return;
    }
    const isNum = /^\d+$/.test(value);
    if (!isNum) {
      this.setState({
        timePerStop: value,
        fieldErrors: {
          ...fieldErrors,
          timePerStop: 'Invalid value',
        },
      });
      return;
    }
    const timePerStop = parseInt(value, 10);
    if (timePerStop < 0) {
      this.setState({
        timePerStop: value,
        fieldErrors: {
          ...fieldErrors,
          timePerStop: 'Invalid value',
        },
      });
      return;
    }
    delete fieldErrors.timePerStop;
    this.setState({
      timePerStop: value,
      fieldErrors: {
        ...fieldErrors,
      },
    });
  }

  onChangeHubDeparture(value: string) {
    const { fieldErrors } = this.state;
    if (value.trim() === '') {
      delete fieldErrors.hubDeparture;
      this.setState({
        hubDeparture: value,
        fieldErrors: {
          ...fieldErrors,
          hubDeparture: 'Invalid value',
        },
      });
      return;
    }
    const isTime = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
    if (!isTime) {
      this.setState({
        hubDeparture: value,
        fieldErrors: {
          ...fieldErrors,
          hubDeparture: 'Invalid value',
        },
      });
      return;
    }

    delete fieldErrors.hubDeparture;
    this.setState({
      hubDeparture: value,
      fieldErrors: {
        ...fieldErrors,
      },
    });
  }

  render() {
    const { fieldErrors, timePerStop, createBundles, hubDeparture } =
      this.state;
    return (
      <Modal
        title="Auto bundle orders"
        closeText="Cancel"
        onClose={() => this.props.onCloseModal()}
        buttons={[
          {
            title: 'Auto bundle',
            className: 'autobundler-options-modal__button',
            onClick: () =>
              this.props.onOptionsSelected(
                parseInt(timePerStop, 10),
                hubDeparture,
                createBundles
              ),
            disabled: Object.keys(fieldErrors).length > 0,
          },
        ]}
      >
        <div className="autobundler-options-modal-content">
          <div
            className={classNames(
              {
                'has-error': !!fieldErrors.timePerStop,
              },
              'autobundler-options-modal-option'
            )}
          >
            <span>Waiting time per stop: </span>
            <span className="autobundler-options-modal-field">
              <input
                type="text"
                name="timePerStop"
                autoFocus={true}
                maxLength={2}
                onChange={event => this.onChangeTimePerStop(event.target.value)}
                value={timePerStop}
                data-lpignore="true"
                placeholder="0"
                autoComplete="off"
              />{' '}
              <small style={{ paddingLeft: 10 }}>minutes</small>
            </span>
          </div>
          <div
            className={classNames(
              {
                'has-error': !!fieldErrors.hubDeparture,
              },
              'autobundler-options-modal-option'
            )}
          >
            <span>Departure time from the Hub: </span>
            <span className="autobundler-options-modal-field">
              <input
                type="text"
                name="hubDeparture"
                maxLength={5}
                onChange={event =>
                  this.onChangeHubDeparture(event.target.value)
                }
                value={hubDeparture}
                data-lpignore="true"
                placeholder="12:00"
                autoComplete="off"
              />
            </span>
          </div>
          {this.props.skipCreate === undefined && (
            <div className="autobundler-options-modal-option">
              <span>Create bundles with results: </span>
              <span className="autobundler-options-modal-field">
                <label>
                  <input
                    type="checkbox"
                    name="createBundles"
                    checked={createBundles}
                    onChange={event => {
                      this.setState({ createBundles: event.target.checked });
                    }}
                  />
                  {createBundles ? 'Yes' : 'Just store routes'}
                </label>
              </span>
            </div>
          )}
        </div>
      </Modal>
    );
  }
}
